// sourceAt is DataHoraEnvio normalized to UTC by the return API importer.
// Display the business date, independently of the browser's timezone.
export function dataEnvioBo(sourceAt) {
  if (!sourceAt) return '-';
  const date = new Date(sourceAt);
  return Number.isFinite(date.getTime())
    ? new Intl.DateTimeFormat('pt-BR', { timeZone: 'America/Sao_Paulo',
      day: '2-digit', month: '2-digit', year: 'numeric' }).format(date) : '-';
}

// Separate display rows, while retaining the authorized local parent PK.
export function pedidoListingRows(orders, kind = 'portal', somenteBackorders = false, statusFilter = null) {
  const portal = kind === 'portal';
  const idField = portal ? 'nrPedido_PW' : 'pedidoId';
  const refField = portal ? 'nrPedido_ERP' : 'pedidoNumeroIntegracao';
  const boMode = backorderMode(somenteBackorders);
  const wanted = String(statusFilter || '').trim().toUpperCase();
  return (orders || []).flatMap(order => {
    const id = order[idField];
    const latestRef = order.atendimento?.referenciaUltimoBO;
    const latest = latestRef
      ? (order.atendimento?.backorders || []).find(bo => bo.referencia === latestRef) : null;
    const row = {
      ...order,
      _listingKey: `pedido:${id}`,
      _referenciaPrincipal: order.atendimento?.referenciaPrincipal || id,
      _isBackorder: boMode,
      _etapaFiscal: 'original',
      NotaFiscal: (order.NotaFiscal || []).filter(nota =>
        /^([A-Z]{2})?[0-9]+$/i.test(String(nota.nr_ped_cli || '').trim())),
      pedidoCategoria: pedidoCategory(order),
      [refField]: boMode ? (latestRef || order[refField])
        : (order.atendimento?.referenciaPrincipal || order[refField]),
    };
    if (!boMode) {
      const parentStatus = pedidoCategory({ ...order, atendimento: null, pedidoCategoria: null });
      const principal = {
      ...row,
      pedidoCategoria: parentStatus,
      ...(portal ? { pedidoSituacao: parentStatus } : {}),
      };
      const stages = [...(order.atendimento?.backorders || [])].sort((a, b) =>
        Number(a.numeroBackorder ?? String(a.referencia).match(/BO(\d+)$/)?.[1] ?? 0)
        - Number(b.numeroBackorder ?? String(b.referencia).match(/BO(\d+)$/)?.[1] ?? 0));
      const seen = new Set();
      const children = stages.filter(bo => {
        if (!bo.referencia || seen.has(bo.referencia)) return false;
        seen.add(bo.referencia);
        return true;
      }).map(bo => pedidoListingRows([{
        ...order, atendimento: { ...order.atendimento, referenciaUltimoBO: bo.referencia, backorders: [bo] },
      }], kind, true)[0]);
      return [principal, ...children].filter(item => !wanted
        || String(item._isBackorder ? item._situacaoBo : parentStatus).trim().toUpperCase() === wanted);
    }
    return {
      ...row,
      _listingKey: `pedido:${id}:bo:${latestRef || 'indisponivel'}`,
      _referenciaPrincipal: row._referenciaPrincipal,
      _etapaFiscal: String(latestRef || '').toUpperCase().match(/BO[0-9]{2}$/)?.[0] || 'invalida',
      NotaFiscal: (order.NotaFiscal || []).filter(nota => {
        const ref = String(nota.nr_ped_cli || '').trim().toUpperCase().match(/^([A-Z]{2})?([0-9]+)(BO[0-9]{2})$/);
        return ref && ref[3] === String(latestRef || '').toUpperCase().match(/BO[0-9]{2}$/)?.[0];
      }),
      pedidoCategoria: latest?.situacaoMac || latest?.situacaoErp || '-',
      valorBoComercial: latest?.valorComercial ?? null,
      valorBoErp: latest?.valorErp ?? null,
      motivoValorBoIndisponivel: latest?.motivoValorIndisponivel
        || (latest ? null : 'Dados comerciais da última BO não disponíveis.'),
      _situacaoBo: latest?.situacaoMac || latest?.situacaoErp || '-',
      // Retain the local PK for detail navigation. Unknown BO values must not
      // inherit the parent's sale, status, invoice or stage dates.
      ...(portal ? {
        pedidoSituacao: latest?.situacaoMac || latest?.situacaoErp || '-',
        valorTotalPedido: null, dataEntrega: null, dataEmissao: dataEnvioBo(latest?.sourceAt),
      } : {
        pedidoSitSales: latest?.situacaoErp || '-',
        pedidoSitComercial: latest?.situacaoMac || latest?.situacaoErp || '-',
        pedidoValorTotal: null, pedidototalcomimpostos: null,
        pedidoDataEntregaPrevista: null, pedidoData: dataEnvioBo(latest?.sourceAt),
      }),
    };
  });
}

export function latestActiveBackorder(order = {}) {
  const latestRef = order.atendimento?.referenciaUltimoBO;
  const latest = latestRef
    ? (order.atendimento?.backorders || []).find(bo => bo.referencia === latestRef) : null;
  return latest && (latest.situacaoMac || latest.situacaoErp) === 'BACK ORDER' ? latest : null;
}

export function pedidoCategory(order = {}) {
  if (order.pedidoCategoria) return order.pedidoCategoria;
  const sales = String(order.pedidoSitSales || order.pedidositsales || '').trim().toUpperCase();
  const commercial = String(order.pedidoSitComercial || order.pedidositcomercial || '').trim().toUpperCase();
  if (['CANCELADO', 'RECUSADO', 'REPROVADO', 'RASCUNHO', 'EM APROVAÇÃO',
    'EM APROVACAO', 'APROVANDO', 'PENDENTE', 'REJEITADO'].includes(sales)) return sales;
  return commercial || sales || order.pedidostatusworkflow || order.pedidoSituacao || order.situacao || 'SEM STATUS';
}

export function backorderMode(value) {
  return value === true || value === 'true' || value === 1 || value === '1';
}

// The backend counts global authorized parents with these same non-status filters.
export function backorderCountFilters(filters = {}) {
  const result = { ...filters };
  ['status', 'pedidoSituacao', 'somenteBackorders', 'contagemBackorders',
    'page', 'pageSize', 'perPage', 'currentPage', 'limit', 'offset'].forEach(key => {
    delete result[key];
  });
  return { ...result, contagemBackorders: true };
}

export function totalBackordersResponse(response) {
  const value = response?.totalBackorders;
  if (!Number.isSafeInteger(value) || value < 0) {
    throw new Error('Resposta inválida na contagem de Back Orders.');
  }
  return value;
}

export function pedidoRowId(row) {
  return `order-row-${encodeURIComponent(row._listingKey)}`;
}

export function valorBoExibido(row) {
  const value = row.valorBoComercial;
  return typeof value === 'number' && Number.isFinite(value)
    ? new Intl.NumberFormat('pt-BR', { style: 'currency', currency: 'BRL' }).format(value) : '—';
}

export function complementoValorBo(row) {
  if (!row._isBackorder) return '';
  const erp = typeof row.valorBoErp === 'number' && Number.isFinite(row.valorBoErp)
    ? new Intl.NumberFormat('pt-BR', { style: 'currency', currency: 'BRL' }).format(row.valorBoErp) : 'não informado';
  return `${motivoValorBo(row) || 'Valor comercial desta BO; não somar ao pedido original.'} Retorno ERP: ${erp}`;
}

export function motivoValorBo(row) {
  if (!row._isBackorder || (typeof row.valorBoComercial === 'number' && Number.isFinite(row.valorBoComercial))) return '';
  if (/^SKU repetido\b/i.test(row.motivoValorBoIndisponivel || '')) return '';
  return row.motivoValorBoIndisponivel || 'Valor comercial da última BO não informado.';
}

export function pedidoCsv(rows, columns) {
  const escape = value => {
    let text = value === null || value === undefined ? '' : String(value);
    text = text.replace(/\r?\n/g, ' ');
    return /[;"]/.test(text) ? `"${text.replace(/"/g, '""')}"` : text;
  };
  return `\ufeff${[
    columns.map(column => escape(column.label)).join(';'),
    ...rows.map(row => columns.map(column => escape(column.get(row))).join(';')),
  ].join('\r\n')}`;
}